import { useNodeViewContext } from '@prosemirror-adapter/react';
import { Button, SimpleSelect } from '@brainfish-ai/components';
import { Trash } from '@phosphor-icons/react';
import type { MouseEvent } from 'react';
import { useState } from 'react';

import { useVariableContentChannels } from '../config';
import { getChannelId, parseDimensions, setChannelId, stringifyDimensions } from '../dimensions';
import { handleBlockControlsKeyDown } from '../keymap';
import { buildChannelSelectOptions, deleteContentVariantAt, findAncestorVariableContent, getSiblingChannelValues, resolveChannelValueForVariableContent } from '../utils';

/**
 * Inner variant: channel selector + delete + editable block content.
 */
export const ContentVariantNodeView = () => {
  const { node, contentRef, setAttrs, view, getPos } = useNodeViewContext();
  const { channels, selectedChannelId } = useVariableContentChannels();
  const value = getChannelId(parseDimensions(node.attrs.dimensions));
  const readonly = !view.editable;
  const stripChrome = readonly && Boolean(selectedChannelId);
  // Bump when opening the toolbar so options reflect sibling channel changes.
  const [optionsEpoch, setOptionsEpoch] = useState(0);

  const pos = typeof getPos === 'function' ? getPos() : undefined;

  if (selectedChannelId && typeof pos === 'number') {
    const ancestor = findAncestorVariableContent(view.state.doc.resolve(pos + 1));
    if (ancestor) {
      const resolved = resolveChannelValueForVariableContent(ancestor.node, selectedChannelId);
      if (resolved == null || value !== resolved) {
        return null;
      }
    }
  }

  const usedBySiblings = typeof pos === 'number' ? getSiblingChannelValues(view, pos) : new Set<string>();
  // optionsEpoch forces a re-read of sibling usage when the toolbar is opened.
  void optionsEpoch;
  const channelOptions = buildChannelSelectOptions(channels, value, usedBySiblings);

  const refreshChannelOptions = () => setOptionsEpoch((epoch) => epoch + 1);

  const handleChannelChange = (nextValue: string) => {
    if (!readonly) {
      setAttrs({ dimensions: stringifyDimensions(setChannelId(nextValue)) });
    }
  };

  const handleDelete = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (readonly) {
      return;
    }

    const variantPos = typeof getPos === 'function' ? getPos() : undefined;
    if (typeof variantPos === 'number') {
      deleteContentVariantAt(view, variantPos);
    }
  };

  if (stripChrome) {
    return <div className="min-w-0" ref={contentRef} />;
  }

  return (
    <div
      className="rounded-md border border-magenta-200 bg-surface p-2"
      data-content-variant
      data-variant-value={value}
    >
      {/*
        Controls must be contentEditable=false so the browser allows keyboard focus
        inside ProseMirror's contenteditable root. stopEvent (below) keeps PM from
        swallowing pointer/keyboard events on these controls.
        Tab from content → this variant's controls (keymap); Tab continues across the
        whole variable_content ring (sibling toolbars + Plus); Esc returns to body.
      */}
      <div
        className="mb-2 flex items-center gap-2 justify-between"
        contentEditable={false}
        data-variant-controls
        data-variant-channel-label
        onKeyDown={(event) => handleBlockControlsKeyDown(event, view)}
        onMouseDown={refreshChannelOptions}
        onFocusCapture={refreshChannelOptions}
        role="toolbar"
        aria-label="Channel selector"
      >
        <SimpleSelect
          className="h-8 text-sm flex-end !border !border-solid !border-dark-400 w-auto"
          value={value}
          options={channelOptions}
          onChange={handleChannelChange}
          disabled={readonly}
          placeholder="Channel"
          appendToBody
        />
        {!readonly && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="text-dark-500 hover:text-destructive"
            aria-label="Delete channel variant"
            data-variant-delete
            onClick={handleDelete}
          >
            <Trash />
          </Button>
        )}
      </div>
      <div className="min-w-0" ref={contentRef} />
    </div>
  );
};
